import LogRocket from 'logrocket';

/**
 * LogRocket utility for user identification and tracking
 */

export const identifyUserWithLogRocket = (user, userProfile, subscription = null) => {
  // Only proceed if LogRocket is available and user data exists
  if (!LogRocket || !user || !userProfile) {
    return;
  }

  try {
    // Prepare user data for LogRocket
    const logRocketUserData = {
      name: userProfile.full_name || user.email?.split('@')[0] || 'Unknown User',
      email: user.email,
      userId: user.id,

      // Custom user variables
      grade: userProfile.grade || 'Not specified',
      role: userProfile.role || 'student',
      userType: userProfile.role === 'admin' ? 'admin' : 'student',
      parentEmail: userProfile.parent_email || null,

      // Account type (since no paid subscriptions currently offered)
      accountType: 'free',
      isAdmin: userProfile.role === 'admin',

      // Account metadata
      createdAt: user.created_at,
      lastSignIn: user.last_sign_in_at,
      emailVerified: user.email_confirmed_at ? true : false,

      // Platform context
      platform: 'MathsCampus',
      environment: process.env.NODE_ENV || 'production'
    };

    // Remove null/undefined values to keep LogRocket data clean
    const cleanUserData = Object.fromEntries(
      Object.entries(logRocketUserData).filter(([_, value]) => value !== null && value !== undefined)
    );

    console.log('🔍 Identifying user with LogRocket:', {
      userId: user.id,
      email: user.email,
      grade: userProfile.grade,
      role: userProfile.role,
      subscriptionType: subscription?.status || 'free'
    });

    // Identify user with LogRocket
    LogRocket.identify(user.id, cleanUserData);

    // Track user login event
    LogRocket.track('User Login', {
      method: 'supabase_auth',
      userType: userProfile.role || 'student',
      grade: userProfile.grade || 'Not specified',
      accountType: 'free',
      isAdmin: userProfile.role === 'admin'
    });

  } catch (error) {
    console.error('Error identifying user with LogRocket:', error);
  }
};

/**
 * Track custom events with LogRocket
 */
export const trackLogRocketEvent = (eventName, properties = {}) => {
  if (!LogRocket) {
    return;
  }

  try {
    LogRocket.track(eventName, {
      timestamp: new Date().toISOString(),
      platform: 'MathsCampus',
      ...properties
    });
  } catch (error) {
    console.error('Error tracking LogRocket event:', error);
  }
};

/**
 * Track user actions and interactions
 */
export const logRocketEvents = {
  // Authentication events
  userSignUp: (userData) => trackLogRocketEvent('User Sign Up', userData),
  userSignOut: () => trackLogRocketEvent('User Sign Out'),
  passwordReset: (email) => trackLogRocketEvent('Password Reset Request', { email }),

  // Assessment events
  testStarted: (testType, grade) => trackLogRocketEvent('Test Started', { testType, grade }),
  testCompleted: (testType, score, grade) => trackLogRocketEvent('Test Completed', { testType, score, grade }),

  // Learning path events
  learningPathAccessed: (topic, grade) => trackLogRocketEvent('Learning Path Accessed', { topic, grade }),
  practiceSessionStarted: (topic, difficulty) => trackLogRocketEvent('Practice Session Started', { topic, difficulty }),
  practiceSessionCompleted: (topic, score, questionsAnswered) => trackLogRocketEvent('Practice Session Completed', { topic, score, questionsAnswered }),

  // Feature engagement events
  featureAccessed: (featureName, context) => trackLogRocketEvent('Feature Accessed', { featureName, context }),

  // Engagement events
  pageView: (pageName) => trackLogRocketEvent('Page View', { pageName }),
  featureUsed: (featureName) => trackLogRocketEvent('Feature Used', { featureName }),

  // Error events
  errorOccurred: (errorType, errorMessage, context) => trackLogRocketEvent('Error Occurred', {
    errorType,
    errorMessage,
    context,
    url: window.location.href
  })
};

export default {
  identifyUserWithLogRocket,
  trackLogRocketEvent,
  logRocketEvents
};